import React, { useState, useEffect, useCallback } from 'react';
import { projectsAllApi, type ProjectDto } from '../../services/api';
import CreateProjectModal from './CreateProjectModal';

function formatPeso(amount: number, compact = false): string {
  if (compact && amount >= 1000) {
    const k = amount / 1000;
    return `₱${k % 1 === 0 ? k : k.toFixed(1)}k`;
  }
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase().replace(/\s+/g, '-');
  const styles: Record<string, string> = {
    created: 'bg-slate-100 text-slate-700',
    planning: 'bg-gray-100 text-gray-700',
    'in-progress': 'bg-sky-100 text-sky-700',
    active: 'bg-emerald-100 text-emerald-700',
    'on-hold': 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        styles[s] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {status}
    </span>
  );
}

export default function AllProjectsPage() {
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectDto | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsAllApi.list();
      if (res.data) setProjects(res.data);
      else setProjects([]);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleProjectCreated = (project: ProjectDto) => {
    setProjects((prev) => [...prev, project]);
    setEditingProject(null);
  };

  const handleProjectUpdated = (project: ProjectDto) => {
    setProjects((prev) => prev.map((p) => (p.id === project.id ? project : p)));
    setEditingProject(null);
  };

  const handleProjectDeleted = async (id: string) => {
    if (!window.confirm('Delete this project? Associated planning items will also be removed. This cannot be undone.')) return;
    try {
      const res = await projectsAllApi.delete(id);
      if (res.success !== false) {
        setProjects((prev) => prev.filter((p) => p.id !== id));
      }
    } catch {
      /* ignore */
    }
    setEditingProject(null);
  };

  const openEditModal = (project: ProjectDto) => {
    setEditingProject(project);
    setModalOpen(true);
  };

  const openAddModal = () => {
    setEditingProject(null);
    setModalOpen(true);
  };

  const filtered = projects.filter(
    (p) =>
      !search ||
      p.project_name.toLowerCase().includes(search.toLowerCase()) ||
      (p.client_name && p.client_name.toLowerCase().includes(search.toLowerCase()))
  );

  const totalProjects = projects.length;
  const activeProjects = projects.filter((p) =>
    ['created', 'in progress', 'planning', 'active'].includes(p.status.toLowerCase())
  ).length;
  const totalBudget = projects.reduce((sum, p) => sum + (p.budget || 0), 0);
  const revenue = projects.reduce((sum, p) => sum + (p.revenue || 0), 0);

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track and manage your projects</p>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Project
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 shrink-0">
        <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalProjects}</p>
              <p className="text-sm text-gray-500 mt-0.5">Total Projects</p>
            </div>
            <span className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeProjects}</p>
              <p className="text-sm text-gray-500 mt-0.5">Active</p>
            </div>
            <span className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-2xl font-bold text-brand-600">{formatPeso(totalBudget, true)}</p>
              <p className="text-sm text-gray-500 mt-0.5">Total Budget</p>
            </div>
            <span className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-2xl font-bold text-emerald-600">{formatPeso(revenue, true)}</p>
              <p className="text-sm text-gray-500 mt-0.5">Revenue</p>
            </div>
            <span className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-white rounded-xl shadow-card border border-gray-100">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-center flex-1">
            <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-500 text-sm">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-16 text-center flex-1">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
            </div>
            <p className="font-semibold text-gray-900 text-lg">No projects yet</p>
            <p className="text-sm text-gray-500 mt-1">Create your first project to start tracking</p>
            <button
              type="button"
              onClick={openAddModal}
              className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Project
            </button>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-gray-100 shrink-0">
              <div className="relative max-w-md">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search projects..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
                />
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/80">
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                      Project
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                      Client
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                      Status
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                      Start
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                      End
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                      Budget
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                      Revenue
                    </th>
                    <th className="w-24 py-3 px-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" scope="col">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-brand-600">{p.project_name}</span>
                        {p.project_type && (
                          <span className="block text-xs text-gray-500 mt-0.5">{p.project_type}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-900">{p.client_name || '—'}</td>
                      <td className="py-3 px-4">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-sm">{formatDate(p.start_date)}</td>
                      <td className="py-3 px-4 text-gray-600 text-sm">{formatDate(p.end_date)}</td>
                      <td className="py-3 px-4 text-right font-medium text-gray-900">
                        {formatPeso(p.budget || 0)}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-emerald-600">
                        {formatPeso(p.revenue || 0)}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            onClick={() => openEditModal(p)}
                            className="p-2 rounded-lg text-gray-400 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                            aria-label="Edit"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleProjectDeleted(p.id)}
                            className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            aria-label="Delete"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="py-12 text-center text-gray-500 text-sm">No projects match your search.</div>
              )}
            </div>
          </>
        )}
      </div>

      <CreateProjectModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingProject(null);
        }}
        onSuccess={editingProject ? handleProjectUpdated : handleProjectCreated}
        editingProject={editingProject}
      />
    </div>
  );
}
